<template>
  <header class="top-navbar gate-navbar flex items-center w-full">
    <router-link :to="{ name: 'in-work' }" class="gate-navbar__logo flex items-center" :aria-label="$t('app.name')">
      <NovatorFullLogo />
    </router-link>

    <div class="flex items-center justify-between gap-05 w-full px-1" style="overflow: hidden">
      <h6 class="mt-0 mb-0 c-white text-ellipsis gate-navbar__section">{{ $t('app.section') }}</h6>

      <div class="flex items-center gap-05 gate-navbar__right">
      <LanguageSwitcher />
      <el-dropdown v-if="_user" class="gate-navbar__dropdown" trigger="click" placement="bottom-end">
        <button type="button" class="gate-navbar__user" :aria-label="$t('label.my_profile')">
          <TheAvatar :name="_user.name ?? ''" :show-name="!_isMobile" color="white" />
        </button>
        <template #dropdown>
          <div class="gate-navbar__menu">
            <div class="border-bottom border-bottom_slate-300 p-1">
              <h6 class="mt-0 mb-05">{{ _user.name }}</h6>
              <p v-if="_user.is_canc" class="mt-0 mb-05 text-sm c-slate-500">{{ $t('label.canc_staff') }}</p>
              <DataCol header="label.iin" :content="_user.iin" />
              <DataCol v-if="_user.bin" header="label.bin" :content="_user.bin" />
            </div>
            <div class="p-05">
              <TheButtonGhost class="w-full justify-start" :icon="SwitchButton" @click="authStore.logout()">
                {{ $t('action.logout') }}
              </TheButtonGhost>
            </div>
          </div>
        </template>
      </el-dropdown>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed } from 'vue'
import { storeToRefs } from 'pinia'
import { SwitchButton } from '@element-plus/icons-vue'
import NovatorFullLogo from '@/assets/images/logos/NovatorFullLogo.vue'
import TheAvatar from '@/components/TheAvatar/TheAvatar.vue'
import DataCol from '@/components/DataDisplay/DataCol.vue'
import TheButtonGhost from '@/components/TheButton/TheButtonGhost.vue'
import LanguageSwitcher from '@/components/LanguageSwitcher.vue'
import { useAuthStore } from '@/stores/auth.store'

/**
 * Верхняя панель — как у Novator (frontend-js `Navbar`): тёмная полоса с логотипом, справа
 * вошедший. Меню по клику, а не по наведению: так оно открывается и с клавиатуры, и на телефоне.
 */
const authStore = useAuthStore()
const { _user } = storeToRefs(authStore)
const _isMobile = computed(() => window.innerWidth <= 600)
</script>

<style scoped lang="scss">
@use '@/assets/styles/base/variables' as *;

.gate-navbar {
  left: 0;
  right: 0;

  &__logo {
    width: 263px;
    height: 56px;
    padding: 0 1.25rem;
    box-sizing: border-box;
    flex-shrink: 0;
    border-right: 1px solid $color-slate-700;
  }

  // Глобальный стиль frontend-js рисует .el-dropdown полем ввода (белый фон, рамка) —
  // на тёмной панели меню пользователя должно быть прозрачным.
  &__dropdown.el-dropdown {
    background: transparent;
    border: none;
  }

  &__user {
    display: flex;
    align-items: center;
    min-height: 40px;
    padding: 4px 8px;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background: rgba(255, 255, 255, 0.1);
    }

    &:focus-visible {
      outline: 2px solid $color-tiffany-400;
    }

    // В узкой панели flex сжимал аватар в овал.
    :deep(.el-avatar) {
      flex-shrink: 0;
    }
  }

  &__right {
    margin-left: auto;
    flex-shrink: 0;
  }

  &__menu {
    min-width: 240px;
  }
}

@media (max-width: 600px) {
  .gate-navbar__logo {
    width: auto;
    border-right: none;
  }

  // На телефоне название раздела не помещается рядом с логотипом и только обрезается —
  // убираем его из потока, чтобы место досталось языку и меню пользователя.
  // !important — общий .text-ellipsis из frontend-js сам ставит display с !important.
  .gate-navbar__section {
    display: none !important;
  }

  .gate-navbar__logo {
    padding: 0 12px;
  }

  // Имя скрыто — отступ справа от аватара лишний.
  .gate-navbar__user :deep(.el-avatar) {
    margin-right: 0;
  }
}

// Совсем узкие экраны (320–400px): логотип, язык и меню должны поместиться в одну строку.
@media (max-width: 400px) {
  .gate-navbar__logo :deep(svg) {
    width: 112px;
    height: auto;
  }

  .gate-navbar__user {
    padding: 4px;
  }
}
</style>
